import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api, fmtErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Scale, ArrowLeft, Coins, CreditCard, ExternalLink, ShieldCheck } from "lucide-react";

const STATUS = {
  active: { label: "Active", cls: "bg-green-100 text-green-700" },
  trialing: { label: "Free trial", cls: "bg-blue-100 text-blue-700" },
  past_due: { label: "Past due", cls: "bg-amber-100 text-amber-700" },
  unpaid: { label: "Unpaid", cls: "bg-red-100 text-red-700" },
  incomplete: { label: "Incomplete", cls: "bg-red-100 text-red-700" },
  canceled: { label: "Canceled", cls: "bg-gray-100 text-gray-700" },
  inactive: { label: "Inactive", cls: "bg-gray-100 text-gray-700" },
};
const fmtDate = (s) => (s ? new Date(s).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" }) : "-");
const daysLeft = (s) => { if (!s) return 0; const d = Math.ceil((new Date(s) - new Date()) / 86400000); return d > 0 ? d : 0; };
const PACK_BLURBS = {
  1: "One minor revision, like small wording or clarity edits to a section.",
  5: "Add a new provision or two (such as business or real estate) with a little to spare.",
  10: "A full state-law review and update, plus several revisions or added provisions.",
  25: "Best value: a major rewrite or comprehensive redraft, with room for ongoing edits.",
};

export default function Billing() {
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [summary, setSummary] = useState(null);
  const [pricing, setPricing] = useState(null);
  const [busy, setBusy] = useState("");

  useEffect(() => { if (user === false) nav("/login"); }, [user, nav]);
  const load = () => {
    api.get("/billing/summary").then((r) => setSummary(r.data)).catch((e) => toast.error(fmtErr(e.response?.data?.detail)));
    api.get("/pricing").then((r) => setPricing(r.data));
  };
  useEffect(() => { if (user) { load(); refresh(); } }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  const go = async (kind, pack_id, key) => {
    setBusy(key);
    try { const { data } = await api.post("/payments/checkout", { kind, pack_id, origin_url: window.location.origin }); window.location.href = data.checkout_url; }
    catch (e) { toast.error(fmtErr(e.response?.data?.detail)); setBusy(""); }
  };
  const portal = async () => {
    setBusy("portal");
    try { const { data } = await api.post("/billing/portal", { origin_url: window.location.origin }); window.location.href = data.url; }
    catch (e) { toast.error(fmtErr(e.response?.data?.detail)); setBusy(""); }
  };

  if (!user || !summary) return <div className="min-h-screen flex items-center justify-center text-[#5C6B73]">Loading…</div>;
  const st = STATUS[summary.subscription_status] || STATUS.inactive;
  const isActive = summary.is_active;
  const isTrial = summary.subscription_status === "trialing";

  return (
    <div className="min-h-screen bg-white" data-testid="billing-page">
      <header className="border-b border-[#E2E8F0] sticky top-0 bg-white z-40">
        <div className="max-w-5xl mx-auto px-5 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2"><Scale className="w-6 h-6 text-[#0B132B]" /><span className="font-display text-xl font-semibold text-[#0B132B]">NuPDocs</span></Link>
          <Link to="/dashboard" data-testid="back-to-dashboard" className="inline-flex items-center gap-1.5 text-sm text-[#5C6B73] hover:text-[#0B132B]"><ArrowLeft className="w-4 h-4" />Dashboard</Link>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-5 py-10">
        <h1 className="font-display text-3xl font-bold text-[#0B132B]">Billing &amp; Credits</h1>
        <p className="mt-2 text-[#5C6B73]">Manage your subscription, buy customization credits, and review your history.</p>

        {/* Subscription */}
        <section className="mt-8 grid md:grid-cols-2 gap-5">
          <div className="border border-[#E2E8F0] rounded-2xl p-6" data-testid="subscription-card">
            <div className="flex items-center justify-between">
              <div className="text-sm font-semibold text-[#0B132B] uppercase tracking-wide">Subscription</div>
              <span className={`text-xs px-3 py-1 rounded-full ${st.cls}`} data-testid="subscription-status">{st.label}</span>
            </div>
            <div className="mt-4 font-display text-2xl font-bold text-[#0B132B]">${pricing?.subscription_price || "18.99"}<span className="text-base font-normal text-[#5C6B73]">/month</span></div>
            {isTrial && (
              <p className="mt-2 text-sm text-[#4A6FA5]" data-testid="trial-info">
                {daysLeft(summary.trial_ends_at)} day{daysLeft(summary.trial_ends_at) === 1 ? "" : "s"} left in your free trial (ends {fmtDate(summary.trial_ends_at)}). Subscribe to keep access after it ends.
              </p>
            )}
            {isActive && summary.subscription_status === "active" && (
              <p className="mt-2 text-sm text-[#5C6B73]" data-testid="next-billing">Next billing date: {fmtDate(summary.next_billing_date)}</p>
            )}
            {["past_due", "unpaid", "incomplete"].includes(summary.subscription_status) && (
              <p className="mt-2 text-sm text-amber-700" data-testid="pastdue-msg">There is a problem with your payment. Your documents are read-only{summary.grace_until ? ` until ${fmtDate(summary.grace_until)}` : ""}. Update your billing to restore full access.</p>
            )}
            {summary.subscription_status === "canceled" && (
              <p className="mt-2 text-sm text-[#5C6B73]">Your subscription is canceled. Your purchased credits are still yours, but you must resubscribe to use them.</p>
            )}
            <div className="mt-5 flex gap-3 flex-wrap">
              {summary.subscription_status !== "active" ? (
                <button onClick={() => go("subscription", null, "sub")} disabled={busy === "sub"} data-testid="subscribe-btn" className="inline-flex items-center gap-2 bg-[#0B132B] text-white px-5 py-2.5 rounded-full text-sm font-medium hover:bg-[#1C2541] transition-colors disabled:opacity-60"><CreditCard className="w-4 h-4" />{busy === "sub" ? "Redirecting…" : "Subscribe now"}</button>
              ) : null}
              <button onClick={portal} disabled={busy === "portal"} data-testid="manage-billing-btn" className="inline-flex items-center gap-2 border border-[#0B132B]/20 text-[#0B132B] px-5 py-2.5 rounded-full text-sm font-medium hover:border-[#0B132B] transition-colors disabled:opacity-60"><ExternalLink className="w-4 h-4" />{busy === "portal" ? "Opening…" : "Manage billing"}</button>
            </div>
          </div>

          <div className="border border-[#E2E8F0] rounded-2xl p-6" data-testid="credit-card">
            <div className="text-sm font-semibold text-[#0B132B] uppercase tracking-wide">Customization credits</div>
            <div className="mt-4 flex items-center gap-2"><Coins className="w-6 h-6 text-[#4A6FA5]" /><span className="font-display text-3xl font-bold text-[#0B132B]" data-testid="credit-balance">{summary.credits}</span><span className="text-[#5C6B73]">available</span></div>
            <p className="mt-2 text-sm text-[#5C6B73]">Credits you purchase are yours to keep and don't disappear if you cancel. You need an active subscription to buy or spend them.</p>
            {!isActive && <p className="mt-2 text-sm text-amber-700" data-testid="credits-locked">Subscribe to purchase or use credits.</p>}
          </div>
        </section>

        {/* Credit packs */}
        {pricing && (
          <section className="mt-10">
            <h2 className="font-display text-xl font-bold text-[#0B132B] mb-4">Buy credits</h2>
            <div className="grid sm:grid-cols-4 gap-4">
              {pricing.credit_packs.map((c) => (
                <div key={c.id} className="border border-[#E2E8F0] rounded-xl p-5 text-center">
                  <div className="font-display text-2xl font-bold text-[#0B132B]">{c.credits}</div>
                  <div className="text-xs text-[#5C6B73]">credit{c.credits > 1 ? "s" : ""} · ${c.price}</div>
                  <p className="text-xs text-[#5C6B73] leading-snug mt-2 mb-3 min-h-[48px]">{PACK_BLURBS[c.credits] || `${c.credits} credits toward custom drafting and revisions.`}</p>
                  <button onClick={() => go("credits", c.id, c.id)} disabled={!isActive || busy === c.id} data-testid={`buy-credits-${c.id}`} className="w-full text-sm border border-[#0B132B]/20 rounded-full py-2 hover:border-[#0B132B] transition-colors disabled:opacity-40 disabled:cursor-not-allowed">{busy === c.id ? "…" : "Buy"}</button>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* History */}
        <section className="mt-10 grid md:grid-cols-2 gap-5">
          <div className="border border-[#E2E8F0] rounded-2xl p-6" data-testid="purchase-history">
            <div className="text-sm font-semibold text-[#0B132B] mb-3">Purchase history</div>
            {summary.purchases.length === 0 ? <p className="text-sm text-[#5C6B73]">No purchases yet.</p> : (
              <div className="space-y-1">
                {summary.purchases.map((l) => (
                  <div key={l.id} className="flex justify-between text-sm border-b border-[#F0F2F5] py-1.5"><span className="text-[#0B132B]">+{l.credits} credits</span><span className="text-[#5C6B73]">{fmtDate(l.date)}</span></div>
                ))}
              </div>
            )}
          </div>
          <div className="border border-[#E2E8F0] rounded-2xl p-6" data-testid="usage-history">
            <div className="text-sm font-semibold text-[#0B132B] mb-3">Usage history</div>
            {summary.usage.length === 0 ? <p className="text-sm text-[#5C6B73]">No credits used yet.</p> : (
              <div className="space-y-1">
                {summary.usage.map((l) => (
                  <div key={l.id} className="flex justify-between text-sm border-b border-[#F0F2F5] py-1.5"><span className="text-[#0B132B]">{l.action}{l.matter_title ? ` · ${l.matter_title}` : ""}</span><span className="text-[#5C6B73] whitespace-nowrap">{Math.abs(l.credits)} · {fmtDate(l.date)}</span></div>
                ))}
              </div>
            )}
          </div>
        </section>

        <div className="mt-10 flex items-start gap-2 text-xs text-[#5C6B73] bg-[#F8F9FA] border border-[#E2E8F0] rounded-xl p-4">
          <ShieldCheck className="w-4 h-4 text-[#4A6FA5] shrink-0 mt-0.5" />
          <span>Payments are processed securely by Stripe. NuPDocs never stores your card details. Stripe holds the customer and payment relationship, while your prenup and postnup content stays inside NuPDocs. See our <Link to="/billing-policy" className="text-[#4A6FA5] hover:text-[#0B132B]">Billing Policy</Link> and <Link to="/credits-policy" className="text-[#4A6FA5] hover:text-[#0B132B]">Credits Policy</Link>.</span>
        </div>
      </main>
    </div>
  );
}
